import React, { useState, useEffect } from "react";
import { Eye, EyeOff, User, Lock, LogIn, Home, Archive, BarChart2, Briefcase } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { authActions } from '../redux/actions';
import Helper1, { notify } from '../utility/Helper1';
import { useSelector, useDispatch } from 'react-redux';
import { WebLink, SiteWide, Images } from "./customComponents";
import { allRemoteSettingsSelect } from "../redux/selectors";



function Signin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const dispatch = useDispatch();
  const [fullPageLoading, setFullPageLoading] = useState(false);

  const navigate = useNavigate();
  const site_settings = useSelector(allRemoteSettingsSelect);

  const doLogin = async (e) => {
    e.preventDefault();
    setFullPageLoading(true);

    dispatch(authActions.onLogin("account/login", { username: email, password }, response => {

      if (response.code != 0) {

        if (response.code == 1) {
          navigate('/');
        } else if (response.code == 2) {
          navigate('/verify-email');
        }
      } else {
        notify(response.msg, response.code)
      }

      setFullPageLoading(false);

    }),
    );
  };

  const handleInputFocus = (field) => {
    setFocusedField(field);
  };

  const handleInputBlur = () => {
    setFocusedField(null);
  };

  return (
    <SiteWide>
      <div style={{backgroundColor: `#233950`}} className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-dark">
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-72 h-72 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
          <div className="absolute top-40 right-20 w-72 h-72 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
          <div className="absolute -bottom-8 left-40 w-72 h-72 bg-gradient-to-r from-green-500 to-cyan-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-500"></div>
        </div>

        {/* Floating particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-white rounded-full opacity-20 animate-bounce"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 2}s`
              }}
            ></div>
          ))}
        </div>

        {/* Main login container */}
        <div className="relative z-10 w-full max-w-md">
          {/* Glowing border effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 rounded-2xl blur-sm opacity-75 animate-pulse"></div>

          <div className="relative bg-gray-900/80 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-gray-700/50">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="relative inline-block">
                <Images
                  height="60px"
                  src={site_settings?.site_settings?.logo || "/api/placeholder/200/60"}
                  alt="logo"
                  className="mx-auto mb-4 filter brightness-110"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-lg blur-lg opacity-30"></div>
              </div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 bg-clip-text text-transparent mb-2">
                Welcome Back
              </h2>
              <p className="text-gray-400 text-sm">Sign in to your account</p>
            </div>

            <div className="space-y-6">
              {/* Username field */}
              <div className="relative group">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Username
                </label>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-lg transition-opacity duration-300 ${focusedField === 'username' ? 'opacity-100' : 'opacity-0'}`}></div>
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 z-10" />
                  <input
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => handleInputFocus('username')}
                    onBlur={handleInputBlur}
                    placeholder="Enter your username"
                    className="relative w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white placeholder-gray-400 transition-all duration-300 backdrop-blur-sm"
                    required
                  />
                </div>
              </div>

              {/* Password field */}
              <div className="relative group">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-lg transition-opacity duration-300 ${focusedField === 'password' ? 'opacity-100' : 'opacity-0'}`}></div>
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 z-10" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => handleInputFocus('password')}
                    onBlur={handleInputBlur}
                    placeholder="Enter your password"
                    className="relative w-full pl-12 pr-12 py-3 bg-gray-800/50 border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder-gray-400 transition-all duration-300 backdrop-blur-sm"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors z-10"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Login button */}
              <button
                type="button"
                disabled={loading}
                onClick={doLogin}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className="relative w-full py-3 px-6 rounded-lg font-semibold text-white transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden group"
              >
                {/* Button background gradient */}
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 transition-opacity duration-300"></div>
                <div className={`absolute inset-0 bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}></div>

                {/* Button glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 blur-lg opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>

                <div className="relative flex items-center justify-center space-x-2">
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <LogIn className="w-5 h-5" />
                      <span>Sign In</span>
                    </>
                  )}
                </div>
              </button>

              {/* Links */}
              <div className="flex flex-col space-y-3 pt-4">
                <div className="flex justify-between items-center text-sm">
                  <WebLink
                    to="/signup"
                    className="text-cyan-400 hover:text-cyan-300 transition-colors duration-200 hover:underline"
                  >
                    Create Account
                  </WebLink>
                  <WebLink
                    to="/reset-password"
                    className="text-purple-400 hover:text-purple-300 transition-colors duration-200 hover:underline"
                  >
                    Forgot Password?
                  </WebLink>
                </div>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-t-2xl"></div>
          </div>
        </div>

        {/* Bottom navigation icons */}
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-20">
          <div className="flex space-x-6 bg-gray-900/70 backdrop-blur-lg px-6 py-3 rounded-full border border-gray-700/50">
            {[
              { icon: Home, to: '/', color: 'text-cyan-400' },
            ].map(({ icon: Icon, color, to }, index) => (
              <button
                onClick={() => navigate(to)}
                key={index}
                className={`${color} hover:scale-110 transition-transform duration-200 p-2 rounded-full hover:bg-white/10`}
              >
                <Icon className="w-5 h-5" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </SiteWide>
  );
}

export default Signin;